import { Controller, useFormContext } from 'react-hook-form';
import { IonicInput } from 'ionic-ui';
import {
  AutoCompleteInput,
  OptionsType,
} from '../../../../../../ui/AutoCompleteInput';
import {
  findCountry,
  findStateInCountry,
} from '../../../../../../../utils/helperFunction/findCountryAndRegion';
import { WalletIndividualKyc } from '../../../../../../../schemas/wallet-individual-kyc-schema';
import getEighteenYearsAgo from '../../../../../../../utils/helperFunction/getEighteenYearsAgo';
import PhoneNumberInput from '../../../../../../ui/PhoneNumberInput';
import { useEffect } from 'react';

type Props = {
  addresses: { value: number; label: string; disabled: boolean }[];
};

export default function IndividualProfileDialog({ addresses }: Props) {
  const { control, watch, setValue, resetField } =
    useFormContext<WalletIndividualKyc>();
  const addressChoice = watch('addressChoice');
  const selectedCountry = watch('newAddress.country')?.id?.trim();

  const getCountry = async (query: string): Promise<OptionsType[]> => {
    const possibleCountries = await findCountry(query);
    return possibleCountries?.map(({ name, code }) => ({
      id: code,
      icon: <span className={`fi fi-${code.toLowerCase()}`}></span>,
      name,
    }));
  };

  const getState = async (query: string): Promise<OptionsType[]> => {
    const possibleStates = await findStateInCountry(query, selectedCountry);
    return possibleStates?.map(({ name }) => ({ id: name, name })) ?? [];
  };

  useEffect(() => {
    resetField('newAddress.state');
  }, [selectedCountry]);

  return (
    <div>
      <h6 className="medium-emphasis-text font-medium">
        Confirm your personal details
      </h6>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
        <Controller
          name="first_name"
          control={control}
          render={({ field, fieldState }) => (
            <div>
              <IonicInput
                {...field}
                label="First name"
                placeholder="E.g. Femi"
                type="text"
                size="lg"
                hintClass="text-red-600 dark:text-red-600"
                value={field.value}
                hint={fieldState.error?.message}
              />
            </div>
          )}
        />
        <Controller
          name="last_name"
          control={control}
          render={({ field, fieldState }) => (
            <div>
              <IonicInput
                {...field}
                label="Last name"
                placeholder="E.g. Otedola"
                type="text"
                size="lg"
                hintClass="text-red-600 dark:text-red-600"
                value={field.value}
                hint={fieldState.error?.message}
              />
            </div>
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field, fieldState }) => (
            <div>
              <PhoneNumberInput
                {...field}
                label="Phone number*"
                size="lg"
                placeholder="e.g. 0801 234 5678"
                hint={fieldState.error?.message}
                hintClass="text-red-600 dark:text-red-600"
              />
            </div>
          )}
        />
        <Controller
          name="dob"
          control={control}
          render={({ field, fieldState }) => {
            const max = getEighteenYearsAgo();
            return (
              <div>
                <IonicInput
                  {...field}
                  label="Date of Birth"
                  placeholder="YY-MM-DD"
                  type="date"
                  size="lg"
                  hintClass="text-red-600 dark:text-red-600"
                  value={field.value?.trim() || max}
                  hint={fieldState.error?.message}
                />
              </div>
            );
          }}
        />
      </div>

      <div className="space-y-4 pb-4 w-full">
        <h6 className="high-emphasis-text font-medium">
          Choose address source
        </h6>
        <div className="flex items-stretch flex-wrap w-full gap-3">
          <div className="flex-1 max-w-full min-w-56 ">
            <button
              type="button"
              disabled={addresses.length < 1}
              onClick={() => setValue('addressChoice', 'saved')}
              className={`text-left space-y-1 w-full p-3 rounded-md border ${
                addressChoice === 'saved'
                  ? 'border-primary bg-primary/5'
                  : 'border-border'
              }`}
            >
              <p className="font-medium high-emphasis-text text-wrap">
                Use saved address
              </p>
              <small className="medium-emphasis-text block text-wrap">
                Pick an address already attached to your account.
              </small>
            </button>
          </div>
          <div className="flex-1 w-full min-w-56 ">
            <button
              type="button"
              onClick={() =>
                setValue('addressChoice', 'new', {
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }
              className={`text-left space-y-1 w-full p-3 rounded-md border ${
                addressChoice === 'new'
                  ? 'border-primary bg-primary/5'
                  : 'border-border'
              }`}
            >
              <p className="font-medium high-emphasis-text text-wrap">
                Add new address
              </p>
              <small className="medium-emphasis-text block text-wrap">
                Enter a different residential address for this wallet.
              </small>
            </button>
          </div>
        </div>

        {addressChoice === 'saved' ? (
          <Controller
            name="savedAddressId"
            control={control}
            render={({ field, fieldState }) => (
              <IonicInput
                {...field}
                type="select"
                size="lg"
                ignorePortal={true}
                label="Saved address*"
                options={addresses}
                placeholder="Select address"
                hint={fieldState.error?.message}
                hintClass="text-red-600 dark:text-red-600"
              />
            )}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              name="newAddress.street"
              control={control}
              render={({ field, fieldState }) => (
                <IonicInput
                  {...field}
                  label="Street*"
                  size="lg"
                  placeholder="e.g. 12 Allen Avenue"
                  hint={fieldState.error?.message}
                  hintClass={fieldState.error ? 'text-destructive' : undefined}
                />
              )}
            />

            <Controller
              name="newAddress.country"
              control={control}
              render={({ field, fieldState }) => (
                <AutoCompleteInput
                  {...field}
                  size="lg"
                  fetchOptions={getCountry}
                  onChange={(value: any) =>
                    setValue(`newAddress.country`, value, {
                      shouldTouch: true,
                      shouldValidate: true,
                    })
                  }
                  label="Country*"
                  placeholder="Enter country"
                  hintMsg={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="newAddress.state"
              control={control}
              render={({ field, fieldState }) => (
                <AutoCompleteInput
                  {...field}
                  size="lg"
                  fetchOptions={getState}
                  onChange={(value: any) =>
                    setValue(`newAddress.state`, value, {
                      shouldTouch: true,
                      shouldValidate: true,
                    })
                  }
                  disabled={!selectedCountry}
                  label="State*"
                  placeholder="Enter state"
                  hintMsg={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="newAddress.city"
              control={control}
              render={({ field, fieldState }) => (
                <IonicInput
                  {...field}
                  size="lg"
                  label="City*"
                  placeholder="Enter city"
                  hint={fieldState.error?.message ?? ''}
                  hintClass={fieldState.error ? 'text-destructive' : undefined}
                />
              )}
            />

            <Controller
              name="newAddress.lga"
              control={control}
              render={({ field, fieldState }) => (
                <IonicInput
                  {...field}
                  size="lg"
                  label="Local government area"
                  placeholder="Enter LGA"
                  hint={fieldState.error?.message}
                  hintClass="text-red-600 dark:text-red-600"
                />
              )}
            />

            <Controller
              name="newAddress.postal_code"
              control={control}
              render={({ field, fieldState }) => (
                <IonicInput
                  {...field}
                  size="lg"
                  label="Postal code"
                  placeholder="Enter postal code"
                  hint={fieldState.error?.message}
                  hintClass="text-red-600 dark:text-red-600"
                />
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}
